import React, { FC, ReactNode } from 'react';
import Icon from '@app/components/ui/icons';
import { IconNameType } from '@app/components/ui/icons/icon-name.type';
import cn from 'classnames';
import { NavLink } from 'react-router-dom';

import PermissionAccessWrapper from '../permission-access-wrapper/permission-access-wrapper';
import CircleButton from '../circle-button';

export type NavigationTabsItem = {
  link?: string;
  icon?: IconNameType;
  label: string;
  index: string | number;
  iconSize?: number;
  soon?: boolean;
  deleteException?: boolean;
  hide?: boolean;
  dataId?: string;
  tag?: string;
  state?: any;
};

type NavigationTabsProps = {
  items: Array<NavigationTabsItem>;
  onChange?: (data: NavigationTabsItem) => void;
  overflow?: boolean;
  conditionalIsActive?: string;
  onDeleteTab?: (index: string) => void;
};

const NavigationTabs: FC<NavigationTabsProps> = (props) => {
  const { items, onChange, overflow, conditionalIsActive, onDeleteTab } = props;
  const tabItemClassName = (isActive: boolean) =>
    cn(
      'inline-flex',
      'gap-[10px] xl:gap-[13px]',
      'select-none',
      'hover:text-1color',
      'items-center',
      'pl-[18px]',
      'pr-[22px]',
      'pt-[12px]',
      'pb-[10px]',
      'text-base',
      'w-full',
      'active:text-primary',
      'truncate',
      isActive ? 'bg-gradient-to-b from-white to-gray text-1color rounded-t-[10px]' : 'text-2color',
    );

  function handlerPushNewPage({ tabItem }: { tabItem: NavigationTabsItem }) {
    onChange?.(tabItem);
  }

  const WrapTabToNavLink = ({
    tabItem,
    children,
    soon,
    dataId,
    state,
  }: {
    tabItem: NavigationTabsItem;
    children: ReactNode;
    soon?: boolean;
    dataId?: string;
    state: any;
  }) => {
    if (tabItem?.link) {
      return (
        <NavLink
          data-id={dataId || ''}
          to={tabItem.link || '/'}
          className={({ isActive }) => cn(tabItemClassName(isActive))}
          end
          state={state}
        >
          {children}
        </NavLink>
      );
    } else {
      return (
        <div
          data-id={dataId || ''}
          className={cn(
            tabItemClassName(conditionalIsActive === tabItem.index),
            'cursor-pointer',
            String(conditionalIsActive === tabItem.index),
            `${soon && 'pointer-events-none text-4color'}`,
          )}
          onClick={handlerPushNewPage.bind(null, { tabItem })}
        >
          {children}
        </div>
      );
    }
  };

  const renderNavigationItem = items.map((tabItem) => {
    return (
      <PermissionAccessWrapper key={tabItem.index} tag={tabItem.tag} permissions={'display'}>
        {!tabItem.hide && (
          <li className="relative group">
            {onDeleteTab && !tabItem.deleteException && (
              <div className="absolute group-hover:visible invisible transition-all right-[2px] top-[2px] z-[99]">
                <CircleButton
                  icon="XIcon"
                  size={12}
                  onClick={() => onDeleteTab(tabItem.index.toString())}
                />
              </div>
            )}
            <WrapTabToNavLink
              dataId={tabItem.dataId}
              tabItem={tabItem}
              soon={tabItem.soon}
              state={tabItem.state}
            >
              {tabItem.icon && (
                <Icon
                  className={cn(`${tabItem.soon ? 'text-4color' : 'text-1color'}`)}
                  size={tabItem.iconSize || 18}
                  name={tabItem.icon}
                />
              )}
              <span className={cn(items.length > 3 && 'hidden xl:block')}>{tabItem.label}</span>

              {tabItem.soon && (
                <div className="self-start text-[9px] ml-[-8px] text-4color">(Скоро)</div>
              )}
            </WrapTabToNavLink>
          </li>
        )}
      </PermissionAccessWrapper>
    );
  });

  return (
    <div className="border-b border-b_medium">
      <ul className={`w-full inline-flex items-center ${overflow && 'overflow-x-auto'}`}>
        {renderNavigationItem}
      </ul>
    </div>
  );
};

export default NavigationTabs;
